import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    X,
    ChefHat,
    Clock,
    Flame,
    Sparkles,
    BookOpen,
    Lightbulb,
    AlertTriangle,
    RefreshCw,
    Zap,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { refreshQuota } from "./QuotaBadge";
import { UploadRemainingBanner } from "./UploadRemainingBanner";
import { formatQty } from "../lib/constants";

/**
 * Dialog "Visualizza ricetta dettagliata".
 * Carica `/recipes/{id}/detail` al mount. Se il dettaglio non è ancora stato
 * generato, propone il pulsante "Genera con AI" che chiama
 * `/recipes/{id}/generate-detail` (Gemini 2.5 Flash).
 */
export function RecipeDetailDialog({ recipe, ingredientsById, onClose }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState("");
    const [quotaExhausted, setQuotaExhausted] = useState(false);
    const isPizza = (recipe.category || "").toLowerCase() === "pizze";
    const [ovenType, setOvenType] = useState("elettrico");
    const navigate = useNavigate();

    const load = async () => {
        setLoading(true);
        setError("");
        try {
            const { data: d } = await api.get(`/recipes/${recipe.id}/detail`);
            setData(d);
            if (d?.oven_type) setOvenType(d.oven_type);
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    const generate = async () => {
        setGenerating(true);
        setError("");
        setQuotaExhausted(false);
        try {
            const body = isPizza ? { oven_type: ovenType } : {};
            const { data: d } = await api.post(
                `/recipes/${recipe.id}/generate-detail`,
                body,
                { timeout: 110000 }
            );
            setData(d);
            toast.success("Ricetta dettagliata generata!");
            refreshQuota();
        } catch (e) {
            const status = e?.response?.status;
            const code = e?.code;
            let msg;
            if (code === "ECONNABORTED" || code === "ERR_NETWORK" || /timeout/i.test(e?.message || "")) {
                msg = "La generazione AI è troppo lunga, riprova tra qualche secondo.";
            } else {
                msg = formatApiError(e);
            }
            if (status === 402) setQuotaExhausted(true);
            setError(msg);
            toast.error(msg);
            refreshQuota();
        } finally {
            setGenerating(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recipe.id]);

    const hasDetail =
        data && (data.instructions?.length > 0 || data.description);

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-brand-navy/40 backdrop-blur-sm px-4 py-6"
            onClick={onClose}
            data-testid="recipe-detail-dialog"
        >
            <div
                className="w-full max-w-3xl max-h-[92vh] bg-paper rounded-2xl shadow-card border border-brand-line overflow-hidden flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="px-6 py-5 border-b border-brand-line flex items-start justify-between gap-4 bg-brand-cream">
                    <div className="min-w-0">
                        <div className="eyebrow text-brand-gold flex items-center gap-1.5">
                            <ChefHat size={11} /> Ricetta dettagliata
                        </div>
                        <h2 className="font-display text-3xl text-brand-ink mt-1 truncate">
                            {recipe.name}
                        </h2>
                        <div className="mt-1 text-[12px] text-brand-muted">
                            {recipe.category} · base {recipe.portions || 1} porzioni
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="h-9 w-9 rounded-full text-brand-muted hover:bg-white transition flex items-center justify-center shrink-0"
                        data-testid="recipe-detail-close"
                    >
                        <X size={16} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto px-6 py-5">
                    {loading ? (
                        <div className="py-16 text-center text-sm text-brand-muted">
                            Caricamento…
                        </div>
                    ) : error ? (
                        <>
                            <div className="rounded-lg bg-red-50 border border-red-200 text-brand-danger text-sm px-4 py-3 flex items-start gap-2">
                                <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                                <span>{error}</span>
                            </div>
                            {quotaExhausted && (
                                <div className="mt-4 rounded-xl border-2 border-brand-gold/50 bg-brand-gold/10 px-4 py-4">
                                    <div className="flex items-start gap-3">
                                        <div className="h-10 w-10 rounded-full bg-brand-gold/30 text-brand-gold flex items-center justify-center shrink-0">
                                            <Zap size={18} />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="font-display text-lg text-brand-ink">
                                                Acquista una ricarica
                                            </div>
                                            <div className="mt-1 text-[13px] text-brand-ink/80">
                                                100 upload extra (80 fatture + 5 menu + 15 ricette AI) a 10€ una tantum. I crediti non scadono.
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    onClose();
                                                    navigate("/billing");
                                                }}
                                                data-testid="recipe-quota-buy-topup"
                                                className="mt-3 inline-flex items-center gap-2 rounded-full bg-brand-gold text-brand-navy px-4 py-2 text-[12px] font-bold hover:bg-brand-goldDark transition"
                                            >
                                                <Sparkles size={12} /> Vai alla ricarica
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            {!hasDetail && (
                                <div className="mb-4">
                                    <UploadRemainingBanner kind="recipes" label="Ricette AI" />
                                </div>
                            )}
                            {!hasDetail ? (
                                <EmptyState
                                    onGenerate={generate}
                                    generating={generating}
                                    isPizza={isPizza}
                                    ovenType={ovenType}
                                    setOvenType={setOvenType}
                                />
                            ) : (
                                <DetailContent
                                    data={data}
                                    ingredientsById={ingredientsById}
                                    recipe={recipe}
                                    onRegenerate={generate}
                                    regenerating={generating}
                                    isPizza={isPizza}
                                    ovenType={ovenType}
                                    setOvenType={setOvenType}
                                />
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function OvenSelector({ value, onChange, small = false }) {
    const options = [
        { id: "elettrico", label: "Forno elettrico", desc: "250-280°C · 6-10 min" },
        { id: "legna", label: "Forno a legna", desc: "400-480°C · 60-90 sec" },
    ];
    return (
        <div data-testid="oven-selector" className={small ? "" : "mt-6"}>
            {!small && (
                <div className="text-[10px] uppercase tracking-[0.16em] text-brand-muted mb-2 text-center">
                    Tipo di forno
                </div>
            )}
            <div className="flex gap-2 justify-center flex-wrap">
                {options.map((o) => {
                    const active = value === o.id;
                    return (
                        <button
                            key={o.id}
                            type="button"
                            onClick={() => onChange(o.id)}
                            data-testid={`oven-option-${o.id}`}
                            className={
                                "inline-flex flex-col items-start text-left rounded-xl border-2 px-4 py-2.5 transition " +
                                (active
                                    ? "border-brand-gold bg-brand-gold/10"
                                    : "border-brand-line bg-white hover:border-brand-gold/40")
                            }
                        >
                            <span
                                className={
                                    "text-sm font-bold " +
                                    (active ? "text-brand-navy" : "text-brand-ink")
                                }
                            >
                                {o.label}
                            </span>
                            <span className="text-[11px] text-brand-muted mt-0.5">
                                {o.desc}
                            </span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

function EmptyState({ onGenerate, generating, isPizza, ovenType, setOvenType }) {
    return (
        <div className="py-10 text-center" data-testid="recipe-detail-empty">
            <div className="mx-auto h-14 w-14 rounded-full bg-brand-gold/15 text-brand-gold flex items-center justify-center mb-4">
                <BookOpen size={26} strokeWidth={1.6} />
            </div>
            <h3 className="font-display text-2xl text-brand-ink">
                Procedimento non ancora disponibile
            </h3>
            <p className="mt-2 text-sm text-brand-muted max-w-md mx-auto">
                Lascia che la nostra AI da chef professionista generi per te il
                procedimento dettagliato in italiano: descrizione, tempi, passi e
                consigli.
                {isPizza && " Per le pizze includerà anche la preparazione dell'impasto."}
            </p>
            {isPizza && (
                <OvenSelector value={ovenType} onChange={setOvenType} />
            )}
            <button
                type="button"
                onClick={onGenerate}
                disabled={generating}
                data-testid="recipe-detail-generate-btn"
                className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand-gold text-brand-navy text-sm font-bold hover:bg-brand-goldDark disabled:opacity-60 transition shadow-gold"
            >
                {generating ? (
                    <>
                        <span className="h-4 w-4 rounded-full border-2 border-brand-navy border-t-transparent animate-spin" />
                        Generazione in corso…
                    </>
                ) : (
                    <>
                        <Sparkles size={14} /> Genera con AI
                    </>
                )}
            </button>
            <div className="mt-3 text-[11px] text-brand-muted">
                Powered by Gemini 2.5 Flash · una sola volta per ricetta
            </div>
        </div>
    );
}

function DetailContent({ data, ingredientsById, recipe, onRegenerate, regenerating, isPizza, ovenType, setOvenType }) {
    return (
        <div className="space-y-6">
            {data.description && (
                <p className="text-[15px] text-brand-ink/90 leading-relaxed font-display italic">
                    {data.description}
                </p>
            )}

            {isPizza && data.oven_type && (
                <div className="rounded-xl border border-brand-gold/30 bg-brand-gold/5 px-4 py-2.5 text-[12px] text-brand-ink flex items-center justify-between gap-3">
                    <span className="flex items-center gap-2">
                        <Flame size={13} className="text-brand-gold" />
                        Generato per <strong>forno {data.oven_type}</strong>
                    </span>
                </div>
            )}

            <div className="grid grid-cols-3 gap-3 text-center">
                <Stat
                    icon={<Clock size={14} />}
                    label="Preparazione"
                    value={data.prep_time_min ? `${data.prep_time_min} min` : "—"}
                />
                <Stat
                    icon={<Flame size={14} />}
                    label="Cottura"
                    value={data.cook_time_min ? `${data.cook_time_min} min` : "—"}
                />
                <Stat
                    icon={<Sparkles size={14} />}
                    label="Difficoltà"
                    value={data.difficulty || "—"}
                />
            </div>

            <Section title="Ingredienti" testid="recipe-detail-ingredients">
                <ul className="divide-y divide-brand-line border border-brand-line rounded-lg bg-white">
                    {(recipe.ingredients || []).map((ri, idx) => {
                        const ing = ingredientsById?.[ri.ingredient_id];
                        const name = ing?.name || ri.ingredient_id;
                        return (
                            <li
                                key={idx}
                                className="px-4 py-2.5 flex items-center justify-between text-sm"
                            >
                                <span className="text-brand-ink">{name}</span>
                                <span className="text-brand-muted font-semibold">
                                    {formatQty(ri.quantity, ri.unit)}
                                </span>
                            </li>
                        );
                    })}
                </ul>
            </Section>

            {data.instructions?.length > 0 && (
                <Section title="Procedimento" testid="recipe-detail-instructions">
                    <ol className="space-y-3">
                        {data.instructions.map((step, idx) => (
                            <li key={idx} className="flex gap-3">
                                <span className="shrink-0 h-7 w-7 rounded-full bg-brand-navy text-white text-[12px] font-bold flex items-center justify-center mt-0.5">
                                    {idx + 1}
                                </span>
                                <p className="text-[14px] text-brand-ink leading-relaxed">
                                    {step}
                                </p>
                            </li>
                        ))}
                    </ol>
                </Section>
            )}

            {data.tips?.length > 0 && (
                <Section
                    title="Consigli dello chef"
                    icon={<Lightbulb size={13} />}
                    testid="recipe-detail-tips"
                >
                    <ul className="space-y-2">
                        {data.tips.map((tip, idx) => (
                            <li
                                key={idx}
                                className="rounded-lg bg-brand-gold/10 border border-brand-gold/30 px-3.5 py-2 text-[13px] text-brand-ink/90"
                            >
                                {tip}
                            </li>
                        ))}
                    </ul>
                </Section>
            )}

            <div className="pt-4 border-t border-brand-line">
                {isPizza && (
                    <div className="mb-3">
                        <div className="text-[10px] uppercase tracking-[0.16em] text-brand-muted mb-2">
                            Rigenera per un forno diverso
                        </div>
                        <OvenSelector value={ovenType} onChange={setOvenType} small />
                    </div>
                )}
                <div className="flex items-center justify-between text-[11px] text-brand-muted">
                    <span>
                        {data.generated_by
                            ? `Generato con ${data.generated_by}`
                            : "Procedimento RENOVA360"}
                    </span>
                    <button
                        type="button"
                        onClick={onRegenerate}
                        disabled={regenerating}
                        className="inline-flex items-center gap-1.5 hover:text-brand-navy transition disabled:opacity-50"
                        data-testid="recipe-detail-regenerate"
                    >
                        <RefreshCw size={11} className={regenerating ? "animate-spin" : ""} />
                        {regenerating ? "Rigenerazione…" : "Rigenera"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function Stat({ icon, label, value }) {
    return (
        <div className="rounded-lg border border-brand-line bg-white px-3 py-2.5">
            <div className="flex items-center justify-center gap-1 text-brand-gold mb-0.5">
                {icon}
                <span className="text-[10px] uppercase tracking-[0.16em] font-bold">
                    {label}
                </span>
            </div>
            <div className="font-semibold text-brand-ink text-sm">{value}</div>
        </div>
    );
}

function Section({ title, icon, testid, children }) {
    return (
        <section data-testid={testid}>
            <h3 className="font-display text-xl text-brand-ink mb-3 flex items-center gap-2">
                {icon && <span className="text-brand-gold">{icon}</span>}
                {title}
            </h3>
            {children}
        </section>
    );
}
